import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import {
  Shield,
  Globe2,
  Sparkles,
  FileUp,
  ArrowRight,
  CheckCircle2,
  MessageSquare,
  FileText,
  Gavel,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const trustBadges = [
  { icon: Globe2, label: "Nationwide Coverage", testId: "badge-nationwide" },
  { icon: Shield, label: "Secure & Confidential", testId: "badge-secure" },
  { icon: Sparkles, label: "AI-Powered Intake", testId: "badge-ai" },
  { icon: FileUp, label: "Document Upload Support", testId: "badge-uploads" },
];

const steps = [
  {
    n: "01",
    title: "Tell Lexi what happened",
    desc: "Describe your situation in your own words. No forms, no jargon.",
    icon: MessageSquare,
  },
  {
    n: "02",
    title: "Lexi asks attorney-specific questions",
    desc: "Dynamic follow-ups tailored to your legal category.",
    icon: Sparkles,
  },
  {
    n: "03",
    title: "Upload supporting documents",
    desc: "Photos, contracts, medical records, police reports — all in one place.",
    icon: FileUp,
  },
  {
    n: "04",
    title: "Receive an organized case summary",
    desc: "A structured, attorney-ready file with timeline, facts, and key details.",
    icon: FileText,
  },
  {
    n: "05",
    title: "Attorneys review matching cases",
    desc: "Qualified attorneys in your state and practice area review your case.",
    icon: Gavel,
  },
];

const categories = [
  "Personal Injury",
  "Auto Accidents",
  "Employment",
  "Family Law",
  "Divorce & Custody",
  "Immigration",
  "Criminal Defense",
  "Contracts & Business",
  "Construction",
  "Probate & Estate",
  "Landlord-Tenant",
  "Workers' Compensation",
  "Medical Malpractice",
  "Consumer Protection",
  "Civil Litigation",
];

export default function HomePage() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What kinds of cases does LexiNetwork handle?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Personal Injury, Car Accidents, Truck Accidents, Motorcycle Accidents, Slip and Fall, Premises Liability, Medical Malpractice, Workers' Compensation, Wrongful Death, Family Law, Criminal Defense, Immigration, Employment Law and Business Law."
        }
      },
      {
        "@type": "Question",
        "name": "How much does the case review cost?",
        "acceptedAnswer": { "@type": "Answer", "text": "Case reviews are free and confidential. There's no obligation to retain an attorney." }
      },
      {
        "@type": "Question",
        "name": "How quickly will an attorney contact me?",
        "acceptedAnswer": { "@type": "Answer", "text": "Attorneys in our network review new cases daily. If they believe they can help, they'll reach out directly." }
      }
    ]
  };
  return (
    <div data-testid="home-page">
      <Seo
        title="LexiNetwork — Get matched with the right attorney"
        description="Free AI-powered legal case review. Connect with attorneys handling personal injury, car accidents, employment, family law and more across the US."
        path="/"
        jsonLd={faqJsonLd}
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-[#F8FAFC]">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059]" />
                <span className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-600">
                  AI-Powered Legal Intake
                </span>
              </div>
              <h1
                data-testid="hero-headline"
                className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[#0A192F] leading-[1.02]"
              >
                Tell Us
                <br />
                <span className="font-serif-display italic font-normal">
                  What Happened.
                </span>
              </h1>
              <p
                data-testid="hero-subheadline"
                className="mt-6 text-lg lg:text-xl leading-relaxed text-slate-600 max-w-2xl"
              >
                Lexi AI organizes your legal situation, identifies key facts,
                gathers supporting information, and prepares an
                attorney-ready case file.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/intake">
                  <Button
                    data-testid="hero-start-case-btn"
                    size="lg"
                    className="bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg px-7 py-6 text-base font-medium group w-full sm:w-auto"
                  >
                    Start Free Case Review
                    <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button
                    data-testid="hero-attorney-login-btn"
                    size="lg"
                    variant="outline"
                    className="rounded-lg px-7 py-6 text-base font-medium border-slate-300 text-[#0A192F] hover:bg-white w-full sm:w-auto"
                  >
                    Attorney Login
                  </Button>
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {trustBadges.map((b) => (
                  <div
                    key={b.label}
                    data-testid={b.testId}
                    className="flex items-center gap-2.5 text-slate-700"
                  >
                    <span className="w-8 h-8 rounded-md bg-[#0A192F]/5 border border-[#0A192F]/10 flex items-center justify-center">
                      <b.icon className="w-4 h-4 text-[#0A192F]" />
                    </span>
                    <span className="text-sm font-medium leading-tight">
                      {b.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="absolute -inset-4 bg-[#0A192F]/5 rounded-3xl rotate-2" />
              <div className="relative bg-white rounded-2xl border border-slate-200 shadow-xl p-6">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <span className="w-9 h-9 rounded-full bg-[#0A192F] flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-[#C5A059]" />
                  </span>
                  <div>
                    <p className="font-heading font-semibold text-[#0A192F]">
                      Lexi
                    </p>
                    <p className="text-xs text-slate-500">
                      Legal intake assistant
                    </p>
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 max-w-[88%]">
                    Hi, I&apos;m Lexi. Tell me what happened in as much detail as
                    possible.
                  </div>
                  <div className="bg-[#0A192F] text-white rounded-lg p-3 text-sm ml-auto max-w-[88%]">
                    I was rear-ended at a stoplight yesterday afternoon. My
                    neck is hurting and I have the other driver&apos;s info.
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 max-w-[88%]">
                    I&apos;m sorry to hear that. A few questions: Was there a
                    police report filed, and have you seen a doctor yet?
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#C5A059]" />
                  Auto Accident — California
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works (short) */}
      <section
        data-testid="how-it-works-section"
        className="bg-white py-24 lg:py-28 border-y border-slate-100"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-4">
              How It Works
            </p>
            <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-[#0A192F] tracking-tight">
              From your story to an{" "}
              <span className="font-serif-display italic font-normal">
                attorney-ready file.
              </span>
            </h2>
          </div>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {steps.map((s) => (
              <div
                key={s.n}
                data-testid={`step-${s.n}`}
                className="group p-6 bg-[#F8FAFC] rounded-xl border border-slate-200 hover:border-[#C5A059] hover:-translate-y-1 transition-all"
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="font-heading text-2xl font-bold text-[#0A192F]/15">
                    {s.n}
                  </span>
                  <span className="w-10 h-10 rounded-lg bg-[#0A192F] group-hover:bg-[#C5A059] transition-colors flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-white" />
                  </span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#0A192F] leading-snug">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 lg:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-4">
                Practice Areas
              </p>
              <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-[#0A192F] tracking-tight">
                Every kind of case, organized.
              </h2>
              <p className="mt-6 text-base text-slate-600 leading-relaxed">
                Lexi supports intake across all major US legal categories.
                The assistant adapts its questions based on the type of issue
                you describe.
              </p>
              <Link to="/intake">
                <Button
                  data-testid="categories-start-btn"
                  className="mt-8 bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg px-6 py-5"
                >
                  Start Your Case Review
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {categories.map((c) => (
                  <div
                    key={c}
                    data-testid={`category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="px-4 py-3 bg-white rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:border-[#C5A059] hover:text-[#0A192F] transition-colors"
                  >
                    {c}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Attorney CTA */}
      <section className="py-24 lg:py-28 bg-[#0A192F] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-4">
              Are you an attorney?
            </p>
            <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-white tracking-tight">
              We&apos;re building a better legal{" "}
              <span className="font-serif-display italic font-normal text-[#C5A059]">
                referral network.
              </span>
            </h2>
            <p className="mt-6 text-base text-slate-300 leading-relaxed max-w-xl">
              Learn about the LexIntake Attorney Network in a dedicated space — it&apos;s kept separate from your client experience.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/attorneys">
                <Button
                  data-testid="attorney-learn-more-btn"
                  className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A192F] rounded-lg px-6 py-5 font-semibold"
                >
                  Visit Attorney Network
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-7">
              <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059]">
                For Legal Consumers
              </p>
              <p className="mt-3 text-white text-lg leading-relaxed">
                Need help with a legal issue? Start a free case review with Lexi above. Attorneys reach out — never the other way around.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0A192F]">
            Ready to tell your story?
          </h2>
          <p className="mt-4 text-slate-600">
            Free case review. No commitment. Talk to Lexi in your own words.
          </p>
          <Link to="/intake">
            <Button
              data-testid="bottom-cta-btn"
              size="lg"
              className="mt-8 bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg px-8 py-6 text-base"
            >
              Start Free Case Review
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
